import React, { useState } from 'react';
import {
  TextField,
  DialogContent,
  DialogActions,
  Tooltip,
  Typography,
  Box,
  // Chip,
} from '@mui/material';
import Buttons from 'components/elements/Buttons';
import { DialogBox } from 'components';
import { unitsStepValue } from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { handleNumberInputKeyDown } from 'utils/GeneralUtil';
import { LoadingButton } from '@mui/lab';
import { YesNoModal } from 'components/shared';
import { zpUserEmail } from 'utils/Constants';
import InfoIcon from '../../assets/images/InfoIcon.png';

interface FundUnit {
  fundId: number;
  units: number;
}

interface UserReallocationData {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  organizationInfo: { companyName: string };
  unitsToReallocate: FundUnit[];
  totalUnitsAllocated: Record<number, string>; // fundId -> totalUnitsAllocated
}

interface FundDetail {
  fundId: number;
  shortDescription: string;
}

interface UnitsReallocationDialogProps {
  open: boolean;
  onClose: () => void;
  userForReallocation: UserReallocationData[];
  allMasterFundDetails: FundDetail[];
  searchValue: string;
  setSearchValue: (v: string) => void;
  handleSearch: (v: string) => void;
  handleFundUnitsChange: (
    userIndex: number,
    fundId: number,
    newUnits: number,
  ) => void;
  isZpUserAdded: boolean;
  handleProceedReAllocation: () => void;
  handleAddZpUser: () => void;
  loader: boolean;
  setLoader: (v: boolean) => void;
}

const formatUnitsForDisplay = (value?: number): string => {
  if (value === undefined || value === null) return '';
  return Number(value).toFixed(7);
};

const getUnitsFieldKey = (userIndex: number, fundId: number): string =>
  `${userIndex}-${fundId}`;

const UnitsReallocationDialog: React.FC<UnitsReallocationDialogProps> = ({
  open,
  onClose,
  userForReallocation,
  allMasterFundDetails,
  searchValue,
  setSearchValue,
  handleSearch,
  handleFundUnitsChange,
  isZpUserAdded,
  handleProceedReAllocation,
  handleAddZpUser,
  loader,
  setLoader,
}) => {
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [unitsInputDrafts, setUnitsInputDrafts] = useState<Record<string, string>>({});

  // Check if any user has invalid unit values (negative resulting balance)
  const hasInvalidUnits = (): boolean => {
    return userForReallocation.some((user) => {
      return user.unitsToReallocate.some((fundUnit) => {
        const totalUnits = parseFloat(
          user.totalUnitsAllocated?.[fundUnit.fundId] ?? '0',
        );
        const unitsToReallocate = fundUnit.units;
        // If units to reallocate is negative and absolute value exceeds total units
        return (
          unitsToReallocate < 0 && Math.abs(unitsToReallocate) > totalUnits
        );
      });
    });
  };

  const handleContinueClick = (): void => {
    setOpenConfirmationDialog(true);
  };

  const handleConfirmAddZpUser = async (): Promise<void> => {
    setOpenConfirmationDialog(false);
    handleAddZpUser();
  };

  const handleCloseConfirmation = (): void => {
    setOpenConfirmationDialog(false);
  };

  return (
    <>
      <DialogBox
        screenWidth="xl"
        open={open}
        onClose={onClose}
        title={strings.displayText.reAllocateUnits}
        fullWidth
        className="units-reallocation-dialog"
      >
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto', p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Search Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <TextField
                label={strings.displayText.enterEmailOrCustomer}
                variant="outlined"
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchValue(e.target.value);
                }}
                sx={{ flex: 1, maxWidth: '500px' }}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' && searchValue.trim()) {
                    handleSearch(searchValue.trim());
                  }
                }}
              />
              <Buttons
                onClick={() => {
                  handleSearch(searchValue.trim());
                }}
                color="primary"
                disabled={searchValue === ''}
                sx={{ minWidth: '100px' }}
              >
                {strings.displayText.add}
              </Buttons>
            </Box>

            {/* Users List */}
            {userForReallocation?.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {userForReallocation.map((user, idx) => (
                  <Box key={user.userId} sx={{ py: 1 }}>
                    {/* Single Row Layout */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        flexWrap: 'nowrap',
                      }}
                    >
                      {/* User Info Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: '150px',
                          flex: '0 0 150px',
                          marginTop: '28px',
                        }}
                      >
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#ffffff',
                                  fontWeight: 500,
                                  mb: 0.5,
                                  fontSize: '13px',
                                }}
                              >
                                <strong>Email:</strong> {user?.email}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#ffffff',
                                  fontWeight: 500,
                                  fontSize: '13px',
                                }}
                              >
                                <strong>Employer:</strong>{' '}
                                {user?.organizationInfo?.companyName || '-'}
                              </Typography>
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              marginTop: '5px',
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                fontSize: '13px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {user?.firstName} {user?.lastName}
                            </Typography>
                            <img
                              src={InfoIcon}
                              alt="info"
                              style={{
                                width: '14px',
                                height: '14px',
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>

                      {/* Funds */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1.5,
                          alignItems: 'center',
                          flex: 1,
                          justifyContent: 'space-between',
                        }}
                      >
                        {allMasterFundDetails
                          .filter((fund) => fund.fundId !== 1) // Exclude fund_id 1 temporarily
                          .map((fund) => {
                            const totalUnits =
                              user.totalUnitsAllocated?.[fund.fundId] ??
                              '0.0000000';
                            const isReadOnly = user?.email === zpUserEmail;
                            const fieldKey = getUnitsFieldKey(idx, fund.fundId);
                            const currentUnits =
                              user.unitsToReallocate.find(
                                (u) => u.fundId === fund.fundId,
                              )?.units;
                            return (
                              <Box
                                key={fund.fundId}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                  flex: '1 1 0',
                                  minWidth: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 0.8,
                                    margin: 'auto',
                                    marginBottom: '8px',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '9px',
                                      fontWeight: 500,
                                      color: 'text.secondary',
                                    }}
                                  >
                                    {strings.displayText.currentUnits} :
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      color: 'primary.main',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {totalUnits}
                                  </Typography>
                                </Box>
                                <TextField
                                  InputProps={{
                                    readOnly: isReadOnly,
                                    inputProps: {
                                      max: parseFloat(totalUnits) || 0,
                                      step: unitsStepValue,
                                    },
                                  }}
                                  type="number"
                                  fullWidth
                                  size="small"
                                  label={fund.shortDescription}
                                  value={
                                    unitsInputDrafts[fieldKey] ??
                                    formatUnitsForDisplay(currentUnits)
                                  }
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    const value = e.target.value;
                                    const normalizedValue =
                                      value.includes('e') || value.includes('E')
                                        ? Number(value).toFixed(7)
                                        : value;

                                    // Allow natural typing, including partial values like "-", ".", "-."
                                    const isEditablePattern =
                                      normalizedValue === '' ||
                                      /^-?\d*(\.\d{0,7})?$/.test(normalizedValue);
                                    if (!isEditablePattern) return;

                                    setUnitsInputDrafts((prev) => ({
                                      ...prev,
                                      [fieldKey]: normalizedValue,
                                    }));

                                    if (
                                      normalizedValue === '-' ||
                                      normalizedValue === '.' ||
                                      normalizedValue === '-.'
                                    ) {
                                      return;
                                    }

                                    // Check if value has more than 7 decimal places
                                    const decimalPlaces = normalizedValue.includes('.')
                                      ? normalizedValue.split('.')[1]?.length ?? 0
                                      : 0;

                                    // Parse the entered value
                                    const numericValue =
                                      parseFloat(normalizedValue) || 0;
                                    const roundedValue =
                                      Math.round(numericValue * 10000000) / 10000000;

                                    // Only validate decimal places
                                    if (decimalPlaces <= 7) {
                                      // Allow any value, validation will show error state
                                      handleFundUnitsChange(
                                        idx,
                                        fund.fundId,
                                        normalizedValue === '' ? 0 : roundedValue,
                                      );
                                    }
                                  }}
                                  onBlur={() => {
                                    setUnitsInputDrafts((prev) => {
                                      const { [fieldKey]: _removedDraft, ...restDrafts } = prev;
                                      return restDrafts;
                                    });
                                  }}
                                  onKeyDown={handleNumberInputKeyDown}
                                  error={(() => {
                                    const currentValue =
                                      user.unitsToReallocate.find(
                                        (u) => u.fundId === fund.fundId,
                                      )?.units ?? 0;
                                    const maxUnits =
                                      parseFloat(totalUnits) || 0;
                                    // Show error if trying to subtract more than available
                                    return (
                                      currentValue < 0 &&
                                      Math.abs(currentValue) > maxUnits
                                    );
                                  })()}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: isReadOnly
                                        ? 'action.disabledBackground'
                                        : 'background.paper',
                                    },
                                  }}
                                />
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Empty State */}
            {userForReallocation?.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body1" sx={{ mb: 1 }}>
                  No users added yet
                </Typography>
                <Typography variant="caption">
                  Search and add users to reallocate units
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {isZpUserAdded ? (
            <LoadingButton
              style={{ width: '220px' }}
              type="submit"
              variant="contained"
              loadingPosition="end"
              loading={loader}
              disabled={hasInvalidUnits()}
              onClick={() => {
                setLoader(true);
                handleProceedReAllocation();
              }}
            >
              {strings.displayText.reAllocateUnits}
            </LoadingButton>
          ) : (
            <Buttons
              color="primary"
              disabled={userForReallocation.length === 0 || hasInvalidUnits()}
              onClick={handleContinueClick}
            >
              {strings.displayText.continueText}
            </Buttons>
          )}
        </DialogActions>
      </DialogBox>
      <YesNoModal
        open={openConfirmationDialog}
        onClose={handleCloseConfirmation}
        title={strings.displayText.areYouConfirmReallocation}
        handleConfirm={handleConfirmAddZpUser}
      />
    </>
  );
};

export default UnitsReallocationDialog;
